import _flowRight from 'lodash/flowRight';
import _unionBy from 'lodash/unionBy';
import { normalize } from 'normalizr';
import {
  getMessagesByOrder,
  createMessageByOrder,
} from '@homeservices/api-kit/workorder/messages/v1';

import { PRO_WORK_ORDER_MATCHES_SCHEMA } from '@/store/modules/entities';
import { toSnakeCase } from '@/utils/to-snake-case';
import {
  withRequestLifecycle,
  REQUEST_LIFECYCLE,
} from '@/utils/request-lifecycle';
import addUTCTimeZoneDesignator from '@/utils/add-utc-time-zone-designator';

const MUTATION_KEY = 'patchMessages';

const emptyArray = [];
const emptyObject = {};

const extractMatch = (jobId, entities) => {
  if (entities.proWorkOrders) {
    const job = entities.proWorkOrders[jobId];
    const matchId = job.matches;
    if (entities.proWorkOrdersMatches && entities.proWorkOrdersMatches[matchId]) {
      return entities.proWorkOrdersMatches[matchId];
    }
  }
  return undefined;
};
const extractMessagesFromJob = (jobId, entities) => {
  const match = extractMatch(jobId, entities);
  return match !== undefined ? match.messages : [];
};
const reconcileMessagesWithinJob = (
  jobId,
  entities,
  messages,
  strategy,
  count
) => {
  const oldMessages = extractMessagesFromJob(jobId, entities) || emptyArray;
  const match = extractMatch(jobId, entities);
  const copiedMatch = JSON.parse(JSON.stringify(match));
  copiedMatch.messages = strategy(oldMessages, messages);
  copiedMatch.messagesTotal = count || copiedMatch.messagesTotal + 1;
  return copiedMatch;
};
const addStrategy = (oldEntities, newEntitiesToAdd) =>
  _unionBy(
    newEntitiesToAdd,
    oldEntities,
    maybeEntity => maybeEntity.message_id || maybeEntity
  );

const initialPartialState = {
  status: REQUEST_LIFECYCLE.UNINITIALIZED,
  error: null,
  result: emptyArray,
  count: 0,
  total: 0,
  hasMore: false,
};

export default {
  state: {
    current: initialPartialState,
  },
  getters: {
    currentJobMessagesMeta: (state) => {
      const { result, ...meta } = state.current;
      return meta;
    },
    getRawJobMessagesByJobId: (
      _,
      __,
      { entities }
    ) => (jobId) => {
      const messages = extractMessagesFromJob(jobId, entities);
      return (messages || emptyArray).filter(
        aMessage => aMessage.message_type !== 'system'
      );
    },
    getGroupedByDateCurrentRawMessagesByJobId: (
      _,
      { getRawJobMessagesByJobId }
    ) => jobId =>
      getRawJobMessagesByJobId(jobId).reduce((acc, message) => {
        const date = new Date(
          addUTCTimeZoneDesignator(message.time_created)
        );

        date.setHours(0, 0, 0, 0);

        const dateGroup = date.toISOString();

        return {
          ...acc,
          [dateGroup]: [...(acc[dateGroup] || []), message],
        };
      }, {}),
  },
  actions: {
    fetchMessages: _flowRight(withRequestLifecycle(MUTATION_KEY))(
      async (
        { rootState: { entities: oldEntities } },
        {
          injected: { performFulfillment },
          jobId,
        }
      ) => {
        const p = { order_hash: jobId, size: 100 };
        const { data } = await getMessagesByOrder(p);

        const { entities } = normalize(
          reconcileMessagesWithinJob(
            jobId,
            oldEntities,
            data.results,
            addStrategy,
            data.total
          ),
          PRO_WORK_ORDER_MATCHES_SCHEMA
        );


        performFulfillment({
          entities,
          result: extractMessagesFromJob(jobId, entities),
          total: data.total,
        });

        return data;
      }
    ),
    createMessages: _flowRight(withRequestLifecycle(MUTATION_KEY))(
      async (
        { rootState: { entities: oldEntities } },
        {
          injected: { performFulfillment },
          jobId,
          params = {},
        }
      ) => {
        const { data } = await createMessageByOrder(jobId, toSnakeCase(params));
        const { entities } = normalize(
          reconcileMessagesWithinJob(
            jobId,
            oldEntities,
            [data],
            addStrategy
          ),
          PRO_WORK_ORDER_MATCHES_SCHEMA
        );

        performFulfillment({
          entities,
          result: extractMessagesFromJob(jobId, entities),
        });

        return data;
      }
    ),
    resetCurrentMessages: ({ commit }) => commit('resetCurrentMessages'),
  },
  mutations: {
    [MUTATION_KEY] (state, payload) {
      const { result } = payload;
      let {
        count = state.current.count,
        total = state.current.total,
        hasMore = state.current.hasMore,
      } = payload;

      if (result) {
        const diff = payload.result.length - state.current.result.length;

        count = result.length;
        total = state.current.total + diff;
        hasMore = result.length < total;
      }

      state.current = {
        ...state.current,
        ...payload,
        count,
        total,
        hasMore,
      };
    },
    resetCurrentMessages (state) {
      state.current = initialPartialState;
      state.current.meta = emptyObject;
    },
  },
};
